import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Bot, 
  CheckCircle2, 
  AlertCircle, 
  Sparkles, 
  Sliders, 
  ArrowRight,
  ShieldCheck,
  Zap,
  HelpCircle,
  TrendingUp
} from "lucide-react";

export default function AuditSimulator() {
  const [domainInput, setDomainInput] = useState("");
  const [selectedChecks, setSelectedChecks] = useState([0, 1]); // default some checks
  const [isSimulatorInView, setIsSimulatorInView] = useState(false);
  const simulatorRef = useRef(null);
  const breakdownRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setIsSimulatorInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSimulatorInView(entry.isIntersecting);
      },
      { threshold: 0.08 }
    );

    if (simulatorRef.current) {
      observer.observe(simulatorRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const auditCriteria = [
    {
      id: 0,
      category: "Technical Foundation",
      title: "Clean URL redirects & zero 404/chain redirect loops",
      weight: 15,
      impact: "High",
      why: "Prevents crawl budget waste and avoids loss of legacy page authority."
    },
    {
      id: 1,
      category: "Structured Data",
      title: "Nested Schema.org JSON-LD (Organization, Product, Author, FAQ)",
      weight: 20,
      impact: "Critical",
      why: "Essential for Google Rich Snippets and LLM entity disambiguation."
    },
    {
      id: 2,
      category: "AEO / AI Search",
      title: "Modular answer chunking & direct factual definition blocks",
      weight: 25,
      impact: "High",
      why: "Allows Claude and ChatGPT to easily cite and extract content in synthesized responses."
    },
    {
      id: 3,
      category: "Entity Authority",
      title: "External verified citations across industry directories & reviews",
      weight: 15,
      impact: "Medium",
      why: "Builds cross-web consensus required for AI Overviews inclusion."
    },
    {
      id: 4,
      category: "CMS & Performance",
      title: "Fast Core Web Vitals (LCP < 2.5s, CLS < 0.1) & mobile hydration",
      weight: 15,
      impact: "Medium",
      why: "Affects mobile-first indexing rankings and user bounce rates."
    },
    {
      id: 5,
      category: "Commercial Pipeline",
      title: "Dedicated BOFU comparison & competitor-alternative pages",
      weight: 10,
      impact: "High",
      why: "Generates high-intent demo bookings and SQLs directly from organic visits."
    }
  ];

  const toggleCheck = (index) => {
    if (selectedChecks.includes(index)) {
      setSelectedChecks(selectedChecks.filter((i) => i !== index));
    } else {
      setSelectedChecks([...selectedChecks, index]);
    }
  };

  const calculateScore = () => {
    const total = selectedChecks.reduce((sum, idx) => sum + auditCriteria[idx].weight, 0);
    return total;
  };

  const score = calculateScore();

  const getScoreGrade = () => {
    if (score >= 80) return { label: "AI Search Ready", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" };
    if (score >= 50) return { label: "Moderate Optimization Needed", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" };
    return { label: "High Vulnerability to AI Disruption", color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/30" };
  };

  const grade = getScoreGrade();

  const scrollToBreakdown = () => {
    if (breakdownRef.current) {
      breakdownRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <section 
      id="simulator" 
      ref={simulatorRef} 
      className="py-8 lg:py-10 bg-[#070b14] border-t border-white/[0.08] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Title */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-5 sm:mb-6"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs font-mono font-bold uppercase tracking-wider text-cyan-400 mb-2 font-semibold">
            <Sliders className="w-3.5 h-3.5 text-cyan-400" />
            Interactive Tool
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl lg:text-[2.25rem] text-white tracking-tight uppercase mb-2 leading-tight">
            AI Search & Technical <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400">Readiness Check</span>
          </h2>
          <p className="text-xs sm:text-sm text-white/75 max-w-2xl mx-auto leading-relaxed">
            Test how well your B2B SaaS website is primed to capture organic search traffic and generative AI citations (ChatGPT, Claude & Google AI Overviews).
          </p>
        </motion.div>

        {/* Simulator Widget Card */}
        <div className="max-w-6xl mx-auto rounded-2xl bg-[#0c1222] border border-white/10 p-5 sm:p-6 shadow-2xl relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-stretch">
            
            {/* Left Column: Interactive Checklist */}
            <div className="lg:col-span-7 flex flex-col justify-between">
              <div className="flex items-center justify-between pb-2.5 border-b border-white/[0.08] mb-2.5">
                <span className="text-xs font-mono uppercase tracking-wider text-white/50 font-semibold">
                  Select Implemented Tactics:
                </span>
                <span className="text-xs font-mono text-cyan-400 font-bold">
                  {selectedChecks.length} of {auditCriteria.length} Active
                </span>
              </div>

              <div className="space-y-2 sm:space-y-2.5">
                {auditCriteria.map((item) => {
                  const isChecked = selectedChecks.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      onClick={() => toggleCheck(item.id)}
                      className={`p-2.5 sm:p-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3 select-none ${
                        isChecked 
                          ? "bg-indigo-950/40 border-cyan-400/50 shadow-[0_0_15px_rgba(6,182,212,0.15)]" 
                          : "bg-[#080d1a]/80 border-white/[0.06] hover:border-white/15 opacity-80"
                      }`}
                    >
                      <div className={`w-4.5 h-4.5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                        isChecked ? "bg-gradient-to-r from-indigo-500 to-cyan-500 text-white shadow-[0_0_10px_rgba(6,182,212,0.5)]" : "bg-white/10 text-transparent"
                      }`}>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="text-[10px] font-mono uppercase tracking-wider text-cyan-400 font-semibold">
                            {item.category}
                          </span>
                          <span className="text-[10px] font-mono text-white/40 flex-shrink-0">+{item.weight} pts</span>
                        </div>
                        <p className="text-xs sm:text-[13px] font-semibold text-white/95 leading-snug">
                          {item.title}
                        </p>
                        <p className="text-[11px] text-white/50 mt-0.5 leading-relaxed">
                          {item.why}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Dynamic Score Gauge & Recommendation */}
            <div 
              ref={breakdownRef}
              className="lg:col-span-5 bg-[#080e1c] p-5 sm:p-5.5 rounded-xl border border-white/[0.08] flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between pb-2.5 border-b border-white/[0.08] mb-3">
                  <span className="text-xs font-mono uppercase tracking-wider text-white/60">Estimated Readiness</span>
                  <Zap className="w-4 h-4 text-amber-400" />
                </div>

                {/* Score Big Display */}
                <div className="flex flex-col items-center justify-center py-3 sm:py-4">
                  <div className="inline-flex items-baseline gap-1">
                    <span className="font-heading font-black text-5xl sm:text-[3.25rem] text-white tracking-tighter leading-none">
                      {score}
                    </span>
                    <span className="text-lg font-mono text-white/40">/100</span>
                  </div>
                  <div className={`mt-2.5 inline-block px-3 py-1 rounded-full text-xs font-mono font-bold uppercase tracking-wider ${grade.bg} ${grade.color} ${grade.border} border`}>
                    {grade.label}
                  </div>
                </div>

                {/* Breakdown analysis */}
                <div className="space-y-2.5 pt-3 border-t border-white/[0.06] text-xs text-white/70">
                  <div className="flex justify-between">
                    <span>Technical Architecture:</span>
                    <span className="font-mono text-white font-semibold">
                      {selectedChecks.includes(0) && selectedChecks.includes(1) ? "Robust" : "Needs Review"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>AI Engine Citations:</span>
                    <span className="font-mono text-white font-semibold">
                      {selectedChecks.includes(2) && selectedChecks.includes(3) ? "Optimized" : "Unclaimed"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pipeline Alignment:</span>
                    <span className="font-mono text-white font-semibold">
                      {selectedChecks.includes(5) ? "Strong" : "At Risk"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Consultation CTA */}
              <div className="mt-4 pt-3.5 border-t border-white/[0.08]">
                <p className="text-xs text-white/75 font-medium mb-2.5 leading-relaxed">
                  Want an end-to-end evaluation for your SaaS domain with a custom HEO roadmap?
                </p>
                <Link
                  to="/ai-visibility-audit/"
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer shadow-lg hover:shadow-cyan-500/25 border border-cyan-400/30"
                >
                  <Sparkles className="w-3.5 h-3.5 text-cyan-200" />
                  <span>Order Full AI Visibility Audit (₹5,000)</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

            </div>

          </div>
        </div>

      </div>

      {/* Mobile Interactive Floating Live Score Pill (Option C) */}
      <div
        className={`fixed bottom-4 inset-x-3 sm:inset-x-6 z-40 lg:hidden transition-all duration-300 transform ${
          isSimulatorInView 
            ? "translate-y-0 opacity-100 pointer-events-auto" 
            : "translate-y-16 opacity-0 pointer-events-none"
        }`}
      >
        <div className="max-w-md mx-auto p-2.5 rounded-2xl bg-[#0b1222]/95 backdrop-blur-xl border border-cyan-400/40 shadow-[0_10px_30px_rgba(0,0,0,0.85),0_0_20px_rgba(6,182,212,0.25)] flex items-center justify-between gap-3">
          {/* Tappable Score & Grade info */}
          <div 
            onClick={scrollToBreakdown}
            className="flex items-center gap-2.5 min-w-0 cursor-pointer flex-1"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/25 to-cyan-500/25 border border-cyan-400/50 flex flex-col items-center justify-center flex-shrink-0 shadow-[0_0_12px_rgba(6,182,212,0.3)]">
              <span className="font-heading font-black text-base text-white leading-none tracking-tight">
                {score}
              </span>
              <span className="text-[8px] font-mono text-cyan-300/80 leading-none mt-0.5 font-bold">
                /100
              </span>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  score >= 80 ? "bg-emerald-400 animate-pulse" : score >= 50 ? "bg-amber-400" : "bg-rose-400"
                }`} />
                <span className={`text-[11px] font-mono font-bold uppercase truncate ${grade.color}`}>
                  {grade.label}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-white/50 font-mono">
                  {selectedChecks.length} of {auditCriteria.length} Active
                </span>
                <span className="text-[9px] text-cyan-400/90 underline font-mono">
                  Breakdown ↓
                </span>
              </div>
            </div>
          </div>

          {/* Quick Action Button */}
          <Link
            to="/ai-visibility-audit/"
            className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 text-white font-mono font-bold text-[11px] uppercase tracking-wider flex items-center gap-1.5 flex-shrink-0 shadow-[0_0_15px_rgba(6,182,212,0.4)] hover:brightness-110 active:scale-95 transition-all border border-cyan-400/40"
          >
            <span>AI Audit</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
